import { Button } from '@src/design-system/button';
import { createRoute } from '@src/core/router/paths';
import { ArrowLeft } from 'iconsax-react';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '@shared/components/reusables/loader';
import { Textfield } from '@shared/fields/text-field';
import { Label } from '@src/design-system/label';
import { CurrencyField } from '@shared/fields/currency-field';
import { Form, Formik, type FormikProps } from 'formik';
import { generateBankFormInitialValue, validationSchema } from './constants';
import { useEditUserBankInfo } from '../../services/edit-user-bank-info';
import type { TBankInfo } from '@shared/types/user';
import { useRef } from 'react';
import memberApi from '@shared/services/member-api';

const EditUserBankInfo = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { data: userData, isLoading } = memberApi.useFetchOne({ id: userId });
  const { mutateAsync, isPending } = useEditUserBankInfo();
  const formBankInfoRef = useRef<FormikProps<TBankInfo>>(null);

  const handleSaveChanges = (values: TBankInfo) => {
    mutateAsync({
      userId: +userId!,
      ...values,
    }).then(() => {
      navigate(createRoute(`/users/${userId}?tab=bankInfo`));
    });
  };

  if (isLoading) return <Loader />;

  if (!userData) return null;
  return (
    <div className='relative bg-primary-30'>
      <div className='flex justify-between bg-white items-center p-6'>
        <h1 className='text-2xl font-semibold text-gray-1000 flex items-center'>
          <Button
            variant='primaryLink'
            size={'iconSm'}
            onClick={() =>
              navigate(createRoute(`/users/${userId}?tab=bankInfo`))
            }
          >
            <ArrowLeft />
          </Button>
          <p className='text-2xl font-semibold text-gray-1000'>
            Edit Bank Info
          </p>
        </h1>
      </div>
      <div className='p-5 min-h-[calc(100vh-240px)] '>
        <Formik
          initialValues={generateBankFormInitialValue(userData)}
          onSubmit={(values) => handleSaveChanges(values)}
          validationSchema={validationSchema}
          innerRef={formBankInfoRef}
        >
          <Form>
            <div className='bg-white rounded-xl p-5 mb-5 flex flex-col gap-4'>
              <p className='text-gray-600 font-medium text-sm mb-2'>
                Bank Address
              </p>
              <Textfield
                name='bank_name'
                type='text'
                placeholder='ex: CIB'
                label='Bank Name'
              />
              <Textfield
                name='account_name'
                type='text'
                placeholder='ex: Ahmed'
                label='Account Name'
              />
              <Textfield
                name='account_number'
                type='number'
                placeholder='Enter Account Number'
                label='Account Number'
              />
              <Textfield
                name='bank_address'
                type='text'
                placeholder='ex: 123 Bank St.'
                label='Bank Address'
              />
              <div>
                <Label>Currency</Label>
                <CurrencyField selectName={'currency'} />
              </div>
              <Textfield
                name='iban'
                type='text'
                placeholder='Enter Routing Number/IBAN'
                label='Routing Number/IBAN'
              />
              <Textfield
                name='swift_code'
                type='text'
                placeholder='ex: 124356'
                label='Swift'
              />
            </div>
          </Form>
        </Formik>
      </div>
      <div className='bg-white border-t-2 z-50 border-pale-border inset-x-0 bottom-0 sticky'>
        <div className='flex items-center py-4 px-5 mx-auto justify-between'>
          <Button
            variant='primaryOutline'
            onClick={() =>
              navigate(createRoute(`/users/${userId}?tab=bankInfo`))
            }
          >
            Cancel
          </Button>

          <Button
            type='button'
            isLoading={isPending}
            onClick={() => {
              formBankInfoRef.current?.submitForm();
            }}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditUserBankInfo;
